import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { useAuthContext } from './context/AuthContext';
import { SocialStatusProvider } from './context/SocialStatusContext';
import CampaignProvider from './components/CampaignProvider/CampaignProvider';
import { shouldShowMobileInterface } from './utils/mobileDetection';
import { MobileApp, MobileLogin } from './components/Mobile';
import { InstallPrompt, OfflineBanner, UpdateNotification } from './components/PWA';
import DesktopApp from './components/desktop/DesktopApp';
import DesktopVerifyEmail from './components/desktop/DesktopVerifyEmail';
import MobileVerifyEmail from './components/Mobile/MobileVerifyEmail';
import LegalRouter from './components/legal/LegalRouter';
import { getLegalRouteKey } from './components/legal/legalRoutes';
import i18n from './i18n';
import { API_CONFIG } from './config/api';
import { STRATEGIZER_ALLOWED_ORIGINS } from './config/strategizer';
import { CapabilityProvider } from './capabilities/CapabilityContext';
import { migrateLegacyMediaUrlsInLocalStorage } from './utils/normalizeMediaUrl';
import { trackPageView, trackScreenView } from './utils/analytics';
import AnalyticsConsent from './components/analytics/AnalyticsConsent';

// Componente di caricamento
const LoadingSpinner = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    fontSize: '16px',
    color: '#ffffff',
    position: 'relative',
    zIndex: 10
  }}>
    {i18n.t('common.loading', { defaultValue: 'Caricamento...' })}
  </div>
);

const detectMobileMode = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return shouldShowMobileInterface();
};

const normalizePathname = (pathname) => {
  if (!pathname || typeof pathname !== 'string') {
    return '/';
  }
  const cleaned = pathname.split('?')[0]?.split('#')[0] || '';
  const trimmed = cleaned.replace(/\/+$/, '');
  return trimmed || '/';
};

const isLandingPath = (normalizedPath) =>
  normalizedPath === '/landing' || normalizedPath.startsWith('/landing/');

const isLoginPath = (normalizedPath) => normalizedPath === '/login';

const isAuthCallbackPath = (normalizedPath) =>
  normalizedPath === '/auth/callback' || normalizedPath.startsWith('/auth/callback/');

const ENABLE_UNAUTH_LANDING_REDIRECT =
  process.env.REACT_APP_ENABLE_UNAUTH_LANDING_REDIRECT !== 'false';

const shouldKeepUnauthPath = (normalizedPath) => {
  if (!ENABLE_UNAUTH_LANDING_REDIRECT && normalizedPath === '/') return true;
  if (normalizedPath === '/verify-email') return true;
  if (isLandingPath(normalizedPath)) return true;
  if (isLoginPath(normalizedPath)) return true;
  if (isAuthCallbackPath(normalizedPath)) return true;
  return Boolean(getLegalRouteKey(normalizedPath));
};

const STRATEGIZER_ORIGINS = STRATEGIZER_ALLOWED_ORIGINS;

const resolveReturnToUrl = (value) => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.startsWith('//')) return null;
  try {
    if (trimmed.startsWith('/')) {
      return new URL(trimmed, window.location.origin);
    }
    return new URL(trimmed);
  } catch {
    return null;
  }
};

const EXTERNAL_RETURN_ALLOWED_ORIGINS = new Set(
  Array.from(STRATEGIZER_ORIGINS).filter(Boolean)
);

function App() {
  const { isAuthenticated, isLoading } = useAuthContext();
  const [isMobileMode, setIsMobileMode] = useState(() => detectMobileMode());
  const returnToHandledRef = useRef(false);

  useEffect(() => {
    migrateLegacyMediaUrlsInLocalStorage(
      ['companyLogoPreview', 'companyLogoRemoteUrl'],
      API_CONFIG.BASE_URL
    );
  }, []);

  // Debug logs for mobile mode
  console.log('🔍 [App] Component rendering with state:', {
    isAuthenticated,
    isLoading,
    isMobileMode
  });

  // Listen for auth expiration events and clear mobile state
  useEffect(() => {
    const handleAuthExpired = () => {
      console.log('[App] Auth expired event received');
    };

    window.addEventListener('auth-expired', handleAuthExpired);

    return () => {
      window.removeEventListener('auth-expired', handleAuthExpired);
    };
  }, []);

  // Log authentication state changes
  useEffect(() => {
    console.log('[App] Authentication state changed:', {
      isAuthenticated,
      isLoading,
      isMobileMode
    });
  }, [isAuthenticated, isLoading, isMobileMode]);
  
  const renderMobileAppView = () => (
    <CapabilityProvider surfaceVariant="mobile" enabled={!isLoading && isAuthenticated}>
      <>
        <SocialStatusProvider>
          <CampaignProvider>
            <MobileApp />
          </CampaignProvider>
        </SocialStatusProvider>

        <InstallPrompt />
        <OfflineBanner />
        <UpdateNotification />
      </>
    </CapabilityProvider>
  );

  const renderMobileLoginView = () => (
    <>
      <MobileLogin />

      <InstallPrompt />
      <OfflineBanner />
      <UpdateNotification />
    </>
  );

  const renderDesktopAppView = () => (
    <CapabilityProvider surfaceVariant="desktop" enabled={!isLoading && isAuthenticated}>
      <DesktopApp />
    </CapabilityProvider>
  );

  const renderDesktopLoginView = () => (
    <CapabilityProvider surfaceVariant="desktop" enabled={false}>
      <DesktopApp />
    </CapabilityProvider>
  );

  // Check for mobile interface on mount and resize
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const checkMobile = () => {
      const shouldShow = detectMobileMode();
      console.log('📱 [App] Mobile detection:', {
        shouldShow,
        userAgent: navigator.userAgent,
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight
      });
      setIsMobileMode(prev => {
        if (prev === shouldShow) {
          return prev;
        }
        return shouldShow;
      });
    };

    console.log('🚀 [App] useEffect - Setting up mobile detection');
    checkMobile();
    
    const handleResize = () => {
      console.log('📏 [App] Window resized, rechecking mobile mode');
      checkMobile();
    };
    window.addEventListener('resize', handleResize);
    
    return () => {
      console.log('🧹 [App] Cleanup - Removing resize listener');
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const trackLegalOrVerify = () => {
      const normalizedPath = normalizePathname(window.location.pathname);
      const legalKey = getLegalRouteKey(normalizedPath);
      if (legalKey) {
        trackPageView({
          screen_name: `legal:${legalKey}`,
          page_title: `Loopino Legal - ${legalKey}`
        });
        return;
      }

      if (normalizedPath === '/verify-email') {
        trackPageView({
          screen_name: 'verify-email',
          page_title: 'Loopino Verify Email'
        });
      }
    };

    trackLegalOrVerify();

    const handlePopState = () => {
      trackLegalOrVerify();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (isMobileMode && !isAuthenticated) {
      trackScreenView('mobile:login');
    }
  }, [isAuthenticated, isLoading, isMobileMode]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isLoading || isAuthenticated) return;
    const normalizedPath = normalizePathname(window.location.pathname);
    if (shouldKeepUnauthPath(normalizedPath)) return;
    window.location.replace('/landing/');
  }, [isAuthenticated, isLoading]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isLoading || !isAuthenticated) return;
    if (returnToHandledRef.current) return;

    const params = new URLSearchParams(window.location.search || '');
    const returnToRaw = params.get('returnTo');
    if (!returnToRaw) return;

    const targetUrl = resolveReturnToUrl(returnToRaw);
    if (!targetUrl) return;

    const isSameOrigin = targetUrl.origin === window.location.origin;
    const isExternalTokenOrigin = EXTERNAL_RETURN_ALLOWED_ORIGINS.has(targetUrl.origin);
    if (!isSameOrigin && !isExternalTokenOrigin) return;

    returnToHandledRef.current = true;

    const finalUrl = isExternalTokenOrigin ? targetUrl.toString() : targetUrl.toString();
    window.location.replace(finalUrl);
  }, [isAuthenticated, isLoading]);

  const renderCurrentInterface = () => {
    if (typeof window !== 'undefined') {
      const normalizedPath = normalizePathname(window.location.pathname);
      if (!isLoading && !isAuthenticated && !shouldKeepUnauthPath(normalizedPath)) {
        return <LoadingSpinner />;
      }
      if (normalizedPath === '/verify-email') {
        return isMobileMode ? <MobileVerifyEmail /> : <DesktopVerifyEmail />;
      }

      const legalKey = getLegalRouteKey(normalizedPath);
      if (legalKey) {
        return <LegalRouter initialPathname={window.location.pathname} />;
      }
    }

    if (isLoading) {
      console.log('⏳ [App] Showing loading spinner - authentication check in progress');
      return <LoadingSpinner />;
    }

    if (isMobileMode) {
      if (isAuthenticated) {
        console.log('📱✅ [App] Rendering MOBILE AUTHENTICATED interface with:', {
          capabilitySurface: 'mobile'
        });
        return renderMobileAppView();
      }

      console.log('📱❌ [App] Rendering MOBILE LOGIN interface - user not authenticated');
      return renderMobileLoginView();
    }

    if (isAuthenticated) {
      console.log('💻✅ [App] Rendering DESKTOP AUTHENTICATED interface');
      return renderDesktopAppView();
    }

    console.log('💻❌ [App] Rendering DESKTOP LOGIN interface - user not authenticated');
    return renderDesktopLoginView();
  };

  return (
    <BrowserRouter>
      {renderCurrentInterface()}
      <AnalyticsConsent isMobile={isMobileMode} />
    </BrowserRouter>
  );
}

export default App;
